import React, { useState, useEffect } from 'react'
import * as yup from 'yup'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { InputAdornment } from '@mui/material'
import { format, parseISO, parse } from 'date-fns'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'

import { theme } from '../../themes/theme.js'
import { formatDateInput } from '../../util/strings.js'
import { DUE_DATE_BUSINESS_DAYS } from '../../util/consts.js'
import { BoldMedBodyText, SmallBodyText } from '../styles/Text.js'
import { IconButton, SecondaryIconButton } from '../common/Button.js'
import { SpaceBetweenRowContainer, FlexColumn } from '../styles/Containers.js'
import CalendarDateButton from '../common/CalendarDateButton.js'
import FormattedInput from '../inputFields/FormattedInput.js'
import CountdownBar from './CountdownBar.js'
import axios from '../../dataClient/dataClient'

const statusColor = {
  default: theme.colors.primary,
  warning: 'rgba(241, 196, 15, 1)',
  critical: 'rgba(232, 76, 61, 1)'
}

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  background: ${props =>
    props.status === 'default'
      ? theme.colors.white
      : `linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), ${statusColor[props.status]}`};
  border: ${props => `1px solid ${statusColor[props.status]}`};
  border-radius: ${theme.borderRadius.div};

  padding: 8px 15px;
  width: 100%;
`

const TargetDate = styled(SmallBodyText)`
  font-style: italic;
`

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`

const schema = yup.object().shape({
  editDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value
    })
    .required('Due date is required')
    .typeError('Please enter a valid date')
})

function DueDate({ dueDate, dueDateId, daysLeft = DUE_DATE_BUSINESS_DAYS, onEdit = () => {} }) {
  const [lastSavedDueDate, setLastSavedDueDate] = useState(dueDate instanceof Date ? dueDate : parseISO(dueDate))
  const [currentDaysLeft, setCurrentDaysLeft] = useState(daysLeft)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setCurrentDaysLeft(daysLeft)
  }, [daysLeft])

  useEffect(() => {
    setLastSavedDueDate(dueDate instanceof Date ? dueDate : parseISO(dueDate))
  }, [dueDate])

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      editDate: format(lastSavedDueDate, 'MMM-dd-yyyy')
    }
  })

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = methods

  const daysRemaining = currentDaysLeft
  const budget = Math.max(DUE_DATE_BUSINESS_DAYS, daysRemaining)

  let status = 'default'
  if (daysRemaining <= 7) status = 'critical'
  else if (daysRemaining <= 14) status = 'warning'

  const handleEdit = () => setIsEditing(true)

  function handleCalendarSelect(newDate) {
    setValue('editDate', format(newDate, 'MMM-dd-yyyy'))
    methods.clearErrors('editDate')
  }

  function onSubmit(data) {
    setLoading(true)
    const now = new Date()
    const newDate =
      data.editDate instanceof Date
        ? new Date(data.editDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds()))
        : new Date(data.editDate).setHours(now.getHours(), now.getMinutes(), now.getSeconds())

    axios
      .patch(`/dueDate/${dueDateId}`, { dueDate: newDate })
      .then(({ data }) => {
        setLastSavedDueDate(parse(format(newDate, 'MMM-dd-yyyy'), 'MMM-dd-yyyy', new Date()))
        setCurrentDaysLeft(data.data.daysLeft)
        setIsEditing(false)
        onEdit({ ...data.data, dueDate: new Date(newDate) })
      })
      .catch(e => {
        console.error('error updating due date', e)
        const message = e.response?.data?.message || 'There was an error updating the due date. Please try again.'
        methods.setError('editDate', { message })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function handleCancel() {
    reset({ editDate: format(lastSavedDueDate, 'MMM-dd-yyyy') })
    setIsEditing(false)
    methods.clearErrors('editDate')
  }

  return (
    <Container>
      <CardContainer status={status} data-testid="due-date-card">
        {!isEditing && (
          <SpaceBetweenRowContainer>
            <BoldMedBodyText>Request Due Date</BoldMedBodyText>
            <TargetDate>{format(lastSavedDueDate, 'MMM-dd-yyyy')}</TargetDate>
          </SpaceBetweenRowContainer>
        )}

        {isEditing ? (
          <FormProvider {...methods}>
            <FormattedInput
              id="editDate"
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarDateButton
                      initialValue={lastSavedDueDate}
                      title="Select Due Date"
                      onConfirm={handleCalendarSelect}
                      disableFuture={false}
                      data-testid="calendar-button"
                    />
                  </InputAdornment>
                )
              }}
              label="Request Due Date"
              handleFormatValue={formatDateInput}
              placeholder="MMM-dd-yyyy"
              disabled={loading}
              error={errors.editDate?.message}
            />
          </FormProvider>
        ) : (
          <CountdownBar daysTaken={budget - daysRemaining} totalDays={budget} progressColor={statusColor[status]} />
        )}
      </CardContainer>

      {isEditing ? (
        <FlexColumn gap="5px">
          <IconButton onClick={handleSubmit(onSubmit)} aria-label="Save" loading={loading}>
            <SaveIcon />
          </IconButton>
          <SecondaryIconButton onClick={handleCancel} aria-label="Cancel" disabled={loading}>
            <CloseIcon />
          </SecondaryIconButton>
        </FlexColumn>
      ) : (
        <SecondaryIconButton onClick={handleEdit} aria-label="Edit due date">
          <EditIcon />
        </SecondaryIconButton>
      )}
    </Container>
  )
}

DueDate.propTypes = {
  dueDate: PropTypes.instanceOf(Date).isRequired,
  dueDateId: PropTypes.string.isRequired,
  daysLeft: PropTypes.number,
  onEdit: PropTypes.func
}

export default DueDate
